
  import { defineComponent, PropType } from 'vue';
  import VChart from 'vue-echarts';
  import type { EChartsOption } from 'echarts';
  import { PieChart } from 'echarts/charts';
  import { TitleComponent, TooltipComponent } from 'echarts/components';
  import { getInstanceByDom, use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';

  import { CHART_COLORS, ECHARTS_LOADING_OPTIONS } from './constants';
  import EChartsBase from './EChartsBase.vue';
  import { TEChartsLoadingOptions, TEChartsTitleConfig } from './types';
  import { normalizeEChartsLabel } from './utils';

  const ruNumberFormatter = new Intl.NumberFormat('ru-RU');
  const PIE_COLORS = [
    '#00ddff',
    '#06b7e3',
    '#0a6bb1',
    '#0382d7',
    '#0068e0',
    '#004ee0',
    '#0657ed',
    '#21adf9',
    '#21eef0',
    '#24afd0',
    '#00fee1',
    '#01fdf9',
    '#99eeee',
    '#90ffff',
    '#34ead9',
    '#55ddf9',
    '#99eeff',
    '#eeeeee',
    '#3fffef',
    '#3effff',
    '#4431ee',
    '#af99ee',
    '#afee99',
    '#afeeee',
    '#afff99',
    '#99ffff',
  ] as const;

  const formatRu = (value: number | string) => ruNumberFormatter.format(Number(value));

  type TChartDonutSeriesItem = {
    name: string;
    count: number;
  };

  type TDataLabels = {
    colors: string[];
    offset: number;
    enebledShadow: boolean;
  };

  type TPiePoint = {
    name: string;
    value: number;
  };

  type TPieVariant = 'donut' | 'pie';
  type TPieLabelMode = 'none' | 'value' | 'percent';
  type TPieTooltipMode = 'value' | 'valueWithPercent';
  type TLegendPosition = 'bottom' | 'right';

  use([TitleComponent, TooltipComponent, PieChart, CanvasRenderer]);

  export default defineComponent({
    name: 'EChartsCircular',
    components: {
      EChartsCore: VChart,
      EChartsBase,
    },
    props: {
      seriesProp: {
        type: Array as PropType<TChartDonutSeriesItem[]>,
        default: () => [],
      },
      titleConfig: {
        type: Object as PropType<TEChartsTitleConfig>,
      },
      template: {
        type: String as PropType<'default' | 'stock'>,
        default: 'default',
      },
      variant: {
        type: String as PropType<TPieVariant>,
        default: 'donut',
      },
      labelMode: {
        type: String as PropType<TPieLabelMode>,
        default: 'none',
      },
      tooltipMode: {
        type: String as PropType<TPieTooltipMode>,
        default: 'value',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      colors: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      dataLabels: {
        type: Object as PropType<TDataLabels>,
        default: (): TDataLabels => ({
          colors: ['#fff'],
          offset: 0,
          enebledShadow: true,
        }),
      },
      isShowLegend: {
        type: Boolean,
        default: true,
      },
      legendColumns: {
        type: Number,
        default: 1,
      },
      legendPosition: {
        type: String as PropType<TLegendPosition>,
        default: 'bottom',
      },
      legendClass: {
        type: String,
        default: '',
      },
      isLegendHoverHighlightEnabled: {
        type: Boolean,
        default: true,
      },
      formatLabel: {
        type: String,
        default: '',
      },
      width: {
        type: Number,
      },
      height: {
        type: Number,
      },
    },
    data() {
      return {
        containerWidth: 0,
        resizeObserver: null as ResizeObserver | null,
      };
    },
    computed: {
      getSafeLegendColumns(): number {
        return Math.max(1, this.legendColumns);
      },
      getBaseChartHeight(): number {
        if (this.height) return this.height;
        if (this.containerWidth <= 0) return 0;
        return Math.round(this.containerWidth * 0.86);
      },
      getPieData(): TPiePoint[] {
        return this.seriesProp.map((seriesItem) => ({
          name: normalizeEChartsLabel(seriesItem.name),
          value: Number(seriesItem.count),
        }));
      },
      getColorPalette(): string[] {
        const pieColors = this.colors.length > 0 ? this.colors : PIE_COLORS;
        return this.colors.length > 0 ? this.colors : [...CHART_COLORS, ...pieColors];
      },
      getLegendItems(): Array<{ name: string; color: string }> {
        return this.getPieData.map((pieItem, index) => ({
          name: pieItem.name,
          color: this.getColorPalette[index % this.getColorPalette.length] ?? CHART_COLORS[0],
        }));
      },
      getPieTotal(): number {
        return this.getPieData.reduce((sum, pieItem) => sum + pieItem.value, 0);
      },
      getLegendGridStyle(): { gridTemplateColumns: string } {
        return {
          gridTemplateColumns: `repeat(${this.getSafeLegendColumns}, minmax(0, 1fr))`,
        };
      },
      getIsEmpty(): boolean {
        return this.getPieData.length === 0 && !this.isLoading;
      },
      getChartOption(): EChartsOption {
        return {
          tooltip: {
            trigger: 'item',
            position: (
              point: number[],
              _params: unknown,
              _dom: unknown,
              _rect: unknown,
              size: { contentSize: number[]; viewSize: number[] },
            ) => {
              const [cursorX, cursorY] = point;
              const [tooltipWidth, tooltipHeight] = size.contentSize;
              const [viewWidth, viewHeight] = size.viewSize;
              const tooltipOffset = 12;

              let left = cursorX - tooltipWidth / 2;
              let top = cursorY - tooltipHeight - tooltipOffset;

              if (left < 0) left = 0;
              if (left + tooltipWidth > viewWidth) left = viewWidth - tooltipWidth;

              if (top < 0) {
                top = cursorY + tooltipOffset;
              }
              if (top + tooltipHeight > viewHeight) {
                top = Math.max(0, viewHeight - tooltipHeight);
              }

              return [left, top];
            },
            formatter: (tooltipParam) => {
              const pointParam = Array.isArray(tooltipParam) ? tooltipParam[0] : tooltipParam;
              if (!pointParam) return '';

              const rawValue = pointParam.value;
              const value = typeof rawValue === 'number' || typeof rawValue === 'string' ? rawValue : 0;
              const marker = 'marker' in pointParam && typeof pointParam.marker === 'string' ? pointParam.marker : '';
              const numericValue = Number(value);

              if (this.tooltipMode === 'valueWithPercent') {
                const percent = this.getPieTotal > 0 ? (numericValue / this.getPieTotal) * 100 : 0;
                return `${marker}${pointParam.name}: ${formatRu(numericValue)}${this.formatLabel} (${percent.toFixed(
                  1,
                )}%)`;
              }

              return `${marker}${pointParam.name}: ${formatRu(numericValue)}${this.formatLabel}`;
            },
          },
          color: this.getColorPalette,
          series: [
            {
              type: 'pie',
              left: 0,
              right: 0,
              top: 2,
              bottom: 2,
              radius: this.variant === 'pie' ? '88%' : ['60%', '88%'],
              center: ['50%', '50%'],
              data: this.getPieData,
              avoidLabelOverlap: true,
              itemStyle: {
                borderWidth: 0,
              },
              labelLine: {
                show: false,
              },
              minAngle: 1,
              stillShowZeroSum: false,
              labelLayout: {
                hideOverlap: true,
              },
              emphasis: {
                scale: false,
                itemStyle: {
                  borderColor: '#ffffff',
                  borderWidth: 2,
                  shadowBlur: 12,
                  shadowColor: 'rgba(0, 0, 0, 0.25)',
                },
              },
              label: {
                show: this.template === 'stock' || this.labelMode !== 'none',
                position: 'inside',
                color: this.dataLabels.colors[0] ?? '#fff',
                fontWeight: 600,
                textBorderWidth: this.dataLabels.enebledShadow ? 2 : 0,
                textBorderColor: this.dataLabels.enebledShadow ? 'rgba(0, 0, 0, 0.35)' : 'transparent',
                formatter: (labelParams) => {
                  const rawValue = labelParams.value;
                  const value = typeof rawValue === 'number' || typeof rawValue === 'string' ? Number(rawValue) : 0;

                  if (this.labelMode === 'percent') {
                    const percent = this.getPieTotal > 0 ? (value / this.getPieTotal) * 100 : 0;
                    return `${percent.toFixed(1)}%`;
                  }

                  return formatRu(value);
                },
              },
            },
          ],
          animationDuration: 250,
          animationDurationUpdate: 250,
        };
      },
      getLoadingOptions(): TEChartsLoadingOptions {
        return ECHARTS_LOADING_OPTIONS;
      },
    },
    mounted() {
      this.updateContainerWidth();

      this.resizeObserver = new ResizeObserver(this.updateContainerWidth);
      this.resizeObserver.observe(this.$el as Element);
    },
    beforeUnmount() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    },
    methods: {
      getChartInstance() {
        const chartRef = this.$refs.echartsCore as
          | {
              chart?: { dispatchAction: Function };
              getEchartsInstance?: () => { dispatchAction: Function };
              $el?: Element;
            }
          | undefined;

        const instanceFromComponent = chartRef?.getEchartsInstance?.() ?? chartRef?.chart;
        if (instanceFromComponent) return instanceFromComponent;

        const rootElement = chartRef?.$el as HTMLElement | undefined;
        if (!rootElement) return undefined;

        const chartDom = rootElement.querySelector('.echarts') as HTMLElement | null;
        if (!chartDom) return undefined;

        return getInstanceByDom(chartDom);
      },
      onLegendItemMouseEnter(index: number) {
        if (!this.isLegendHoverHighlightEnabled) return;
        const chart = this.getChartInstance();
        if (!chart) return;

        chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: index });
      },
      onLegendItemMouseLeave(index: number) {
        if (!this.isLegendHoverHighlightEnabled) return;
        const chart = this.getChartInstance();
        if (!chart) return;

        chart.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: index });
      },
      updateContainerWidth() {
        const rootElement = this.$el as HTMLElement | null;
        if (!rootElement) return;
        this.containerWidth = rootElement.clientWidth;
      },
    },
  });
